import { injectable, inject } from 'inversify';
import { Message, Field, Type } from "protobufjs/light";
import { Result, ok, err } from '@xlearn/common'
import * as SerializationApi from '@/app/state-serialization/api'
import { ProtobufSerializer } from '@/app/state-serialization/infrastructure';
import { QuestionPhase } from '@/app/question/domain/state-machine';
import { QuestionInteractionState } from './question-interaction-state';
import { QuestionStateProtobufSerializer } from '@/app/question/tracking/serialization';

@injectable()
export class QuestionInteractionStateProtobufSerializer implements SerializationApi.StateSerializationPlugin<QuestionInteractionState> {

  key = { id: 39, name: { ns: 'interaction-suspend-data', name: 'question' }, format: 'protobuf' }

  constructor(
    @inject(QuestionStateProtobufSerializer)
    private contentSerializer: QuestionStateProtobufSerializer) {
  }
  
  serialize(input: QuestionInteractionState): Result<Uint8Array, SerializationApi.SerializerError> {

    const stateResult = this.contentSerializer.serializeContent(input.type, 'state', input.state)    
    const answerResult = this.contentSerializer.serializeContent(input.type, 'answer', input.answer)
    if (!stateResult.success || !answerResult.success) {
      return ok(new Uint8Array())
    }
    
    const message = new QuestionInteractionStateProtoMessage({
      type: input.type,
      state: stateResult.value,
      answer: answerResult.value,
      phase: mapPhase(input.phase),
      score: input.result.score
    })

    return ProtobufSerializer.serialize<QuestionInteractionStateProtoMessage>(message,
                                                                              QuestionInteractionStateProtoMessage)

    function mapPhase(phase: QuestionPhase): QuestionPhaseProtoEnum {
      switch (phase) {
        case 'not-answered':
          return QuestionPhaseProtoEnum.none
        case 'answered':
          return QuestionPhaseProtoEnum.answered
        case 'evaluated':
          return QuestionPhaseProtoEnum.evaluated
        case 'solution-shown':
          return QuestionPhaseProtoEnum.solutionShown
      }
    }
  }

  deserialize(content: Uint8Array): Result<QuestionInteractionState, SerializationApi.DeserializerError> {

    const deserializeResult = ProtobufSerializer.deserialize<QuestionInteractionStateProtoMessage>(content,
                                                                                                   QuestionInteractionStateProtoMessage)

    if (!deserializeResult.success) {
      return deserializeResult
    }

    const input = deserializeResult.value
    
    const state = this.contentSerializer.deserializeContent(input.type, 'state', input.state)
    const answer = this.contentSerializer.deserializeContent(input.type, 'answer', input.answer)

    if (!state.success || !answer.success) {
      const result: QuestionInteractionState = {
        type: input.type,
        phase: 'not-answered',
        state: null,
        answer: null,
        result: {
          score: 0
        }
      }
      return ok(result)
    }

    
    const phase = unmapPhase(input.phase)

    const result: QuestionInteractionState = {
      type: input.type,
      state: state.value,
      answer: answer.value,
      phase: phase,
      result: {
        score: input.score
      }
    }

    return ok(result)

    function unmapPhase(input: QuestionPhaseProtoEnum): QuestionPhase {
      switch (input) {
        case QuestionPhaseProtoEnum.none:
          return 'not-answered'
        case QuestionPhaseProtoEnum.answered:
          return 'answered'
        case QuestionPhaseProtoEnum.evaluated:
          return 'evaluated'
        case QuestionPhaseProtoEnum.solutionShown:
          return 'solution-shown'
      }
    }
  }
}

enum QuestionPhaseProtoEnum {
  none = 0,
  answered = 1,
  evaluated = 2,
  solutionShown = 3
}

@Type.d('QuestionInteractionStateProtoMessage')
class QuestionInteractionStateProtoMessage extends Message<QuestionInteractionStateProtoMessage> {

  @Field.d(1, 'string', "optional", '')
  type: string
  
  @Field.d(2, QuestionPhaseProtoEnum, "optional", QuestionPhaseProtoEnum.none)
  phase: QuestionPhaseProtoEnum
  
  @Field.d(3, 'bytes', "optional", [])
  state: Uint8Array
  
  @Field.d(4, 'bytes', "optional", [])
  answer: Uint8Array

  @Field.d(5, 'float', "optional", 0)
  score: number
}
